import { Header } from "./components/layouts/Header";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { LINKS } from "./constants/menu";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, PrivateRoute, PublicRoute } from "./modules/user/services/AuthProvider";
import { TABLE_NAME } from "./utils/constant";
import "./index.css";
import "@fontsource/asap-condensed";
import { ThemeProvider } from "@mui/material";
import theme from "./theme";
import { E_FUNCTIONALITY } from "./utils/functionalityVariable";

import { SigninPage } from "./modules/auth/components/Signin";
import { RegisterPage } from "./modules/auth/components/Register";
import { ActiveAcount } from "./modules/auth/components/ActiveAcount";
import { Home } from "./modules/home/components/Home";
import { NotFoundPage } from "./NotFoundPage";
import { NavigationLoader } from "./components/Loader/NavigationLoader";
import { StockAdjustment } from "./modules/warehouse/components/stock/adjustment/StockAdjustment";

const UserProfile        = lazy(() => import("./modules/user/components/UserProfile/UserProfile"));
const UserTable          = lazy(() => import("./modules/user/components/User").then(m => ({ default: m.UserTable })));
const Department         = lazy(() => import("./modules/user/components/department/Department").then(m => ({ default: m.Department })));
const Role               = lazy(() => import("./modules/user/components/role/Role").then(m => ({ default: m.Role })));
const OrderAccessManagement = lazy(() => import("./modules/orders/components/access/OrderAccessManagement").then(m => ({ default: m.OrderAccessManagement })));
const Functionality      = lazy(() => import("./modules/user/components/functionality/Functionality").then(m => ({ default: m.Functionality })));
const Supplier           = lazy(() => import("./modules/user/components/supplier/Supplier").then(m => ({ default: m.Supplier })));
const GestionMachine     = lazy(() => import("./modules/user/components/gestion-machine/gestion-machine").then(m => ({ default: m.GestionMachine })));
const GestionYarnComposition= lazy(() => import("./modules/user/components/gestion-yarn-compo/gestion-yarn-compo").then(m => ({ default: m.GestionYarnComposition })));
const GestionYarnCount   = lazy(() => import("./modules/user/components/gestion-yarn-count/gestion-yarn-count").then(m => ({ default: m.GestionYarnCount })));
const OptionalScanSteps  = lazy(() => import("./modules/user/components/OptionalScanSteps").then(m => ({ default: m.OptionalScanSteps })));
const ActivateScan       = lazy(() => import("./modules/user/components/ActivateScan").then(m => ({ default: m.ActivateScan })));

const YarnOrderList      = lazy(() => import("./modules/raw-material/purchasing/yarn/YarnOrderList"));
const AccessoryOrderList = lazy(() => import("./modules/raw-material/purchasing/accessory/AccessoryOrderList"));
const SubYarnOrderList   = lazy(() => import("./modules/raw-material/purchasing/sub-yarn/SubYarnOrderList"));
const FollowPage         = lazy(() => import("./modules/raw-material/yarn-follow-up/FollowPage").then(m => ({ default: m.FollowPage })));
const YarnPL             = lazy(() => import("./modules/raw-material/Packing_list/YarnPL").then(m => ({ default: m.YarnPL })));
const FcdYarnDye         = lazy(() => import("./modules/fcd/yarn_dye/FcdYarnDye").then(m => ({ default: m.FcdYarnDye })));
const PieceDye           = lazy(() => import("./modules/fcd/Piece_dye/piece_dye"));
const YarnDyeReceipe     = lazy(() => import("./modules/fcd/gestion-receipe/yarn-dye-receipe"));
const PieceDyeReceipe    = lazy(() => import("./modules/fcd/gestion-receipe/piece-dye-receipe"));
const GestionStockReceipe = lazy(()=> import("./modules/fcd/gestion-stock/gestion-stock-receipe"));
const RapportGestionReceipe = lazy(()=> import ("./modules/fcd/gestion-stock/rapport-gestion-receipe"));

const OrderHead          = lazy(() => import("./modules/orders/components/order/OrderHead"));
const OrderStyle         = lazy(() => import("./modules/orders/components/order/OrderStyle"));
const Booking            = lazy(() => import("./modules/orders/components/booking/Booking").then(m => ({ default: m.Booking })));

const Pdms               = lazy(() => import("./modules/pdms/components/Pdms").then(m => ({ default: m.Pdms })));
const ProductionTicketScan = lazy(() => import("./modules/pdms/components/ProductionTicketScan"));
const TicketScanDetails  = lazy(() => import("./modules/pdms/components/TicketDetails"));
const MissingPieces      = lazy(() => import("./modules/pdms/components/MissingPiecesDetails"));
const LeftOver           = lazy(() => import("./modules/pdms/components/LeftOver").then(m => ({ default: m.LeftOver })));
const LeftOverCreate     = lazy(() => import("./modules/pdms/components/LeftOverCreate").then(m => ({ default: m.LeftOverCreate })));
const QuantityTaken      = lazy(() => import("./modules/pdms/components/QuantityTaken").then(m => ({ default: m.QuantityTaken })));
const QuantityTakenCreate = lazy(() => import("./modules/pdms/components/QuantityTakenCreate").then(m => ({ default: m.QuantityTakenCreate })));

const YarnPMList         = lazy(() => import("./modules/raw-material/purchasing-management/components/yarn/YarnPMList"));
const AccessoryPMList    = lazy(() => import("./modules/raw-material/purchasing-management/components/accessory/AccessoryPMList"));
const LspPMList          = lazy(() => import("./modules/raw-material/purchasing-management/components/lsp/LspPMList"));

const YarnInvoice        = lazy(() => import("./modules/warehouse/components/invoice/yarn/YarnInvoice").then(m => ({ default: m.YarnInvoice })));
const AccessoryInvoice   = lazy(() => import("./modules/warehouse/components/invoice/accessory/AccessoryInvoice").then(m => ({ default: m.AccessoryInvoice })));
const YarnReceivedInvoice    = lazy(() => import("./modules/warehouse/components/invoice/received-invoice/yarn/YarnReceivedInvoice").then(m => ({ default: m.YarnReceivedInvoice })));
const AccessoryReceivedInvoice = lazy(() => import("./modules/warehouse/components/invoice/received-invoice/accessory/AccessoryReceivedInvoice").then(m => ({ default: m.AccessoryReceivedInvoice })));

const DeliveryNoteListe      = lazy(() => import("./modules/warehouse/components/delivery/yarn/DeliveryNoteListe"));
const GarmentDNotesListe = lazy(() => import("./modules/warehouse/components/delivery/garment/DeliveryNoteListe"));
const AccDeliveryNoteListe   = lazy(() => import("./modules/warehouse/components/delivery/accessory/AccDeliveryNoteListe"));
const FcdDeliveryNoteListe   = lazy(() => import("./modules/warehouse/components/delivery/fcd/FcdDeliveryNoteListe"));

const YarnStock          = lazy(() => import("./modules/warehouse/components/stock/yarn/YarnStock").then(m => ({ default: m.YarnStock })));
const AccessoryStock     = lazy(() => import("./modules/warehouse/components/stock/accessory/AccessoryStock").then(m => ({ default: m.AccessoryStock })));
const LycraStock         = lazy(() => import("./modules/warehouse/components/stock/lycra/LycraStock").then(m => ({ default: m.LycraStock })));
const FcdStock           = lazy(() => import("./modules/warehouse/components/stock/fcd/FcdStock").then(m => ({ default: m.FcdStock })));
const GarmentStock       = lazy(() => import("./modules/warehouse/components/stock/garment/GarmentStock").then(m => ({ default: m.GarmentStock })));

const YarnRequirementAllocation = lazy(() =>
  import('./modules/pdms/components/YarnRequirementAllocation').then((module) => ({
    default: module.YarnRequirementAllocation,
  })),
);
const YarnRequirementAllocationDetails = lazy(() =>
  import('./modules/pdms/components/YarnRequirementAllocationDetails').then(
    (module) => ({ default: module.YarnRequirementAllocationDetails }),
  ),
);

const queryClient = new QueryClient();

function App() {
  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AuthProvider>
            <ToastContainer />
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <NavigationLoader />
              <Header />
              <Suspense fallback={null}>
                <Routes>

                  <Route path={LINKS.SIGNIN}           element={<PublicRoute element={<SigninPage />} />} />
                  <Route path={LINKS.SIGNUP}           element={<PublicRoute element={<RegisterPage />} />} />
                  <Route path={LINKS.ACTIVATE_ACCOUNT} element={<ActiveAcount />} />

                  <Route path={LINKS.HOME}             element={<PrivateRoute element={<Home />} />} />
                  <Route path={LINKS.PROFILE}          element={<PrivateRoute element={<UserProfile />} />} />
                  <Route path={LINKS.ADMIN_USER}       element={<PrivateRoute element={<UserTable />} />} />
                  <Route path={LINKS.DEPARTMENT}       element={<PrivateRoute element={<Department />} />} />
                  <Route path={LINKS.ROLE}             element={<PrivateRoute element={<Role />} />} />
                  <Route path={LINKS.ORDER_ACCESS}     element={<PrivateRoute element={<OrderAccessManagement />} user_functionalities={[E_FUNCTIONALITY.menu_setting_role]} />} />
                  <Route path={LINKS.FUNCTIONALITY}    element={<PrivateRoute element={<Functionality />} />} />
                  <Route path={LINKS.SUPPLIER}         element={<PrivateRoute element={<Supplier />} />} />
                  <Route path={LINKS.GESTION_MACHINE}  element={<PrivateRoute element={<GestionMachine />} />} />
                  <Route path={LINKS.GESTION_YARN_COMPO}  element={<PrivateRoute element={<GestionYarnComposition />} />} />
                  <Route path={LINKS.GESTION_YARN_COUNT}  element={<PrivateRoute element={<GestionYarnCount />} />} />
                  <Route path={LINKS.OPTIONAL_SCAN_STEPS_ADMIN} element={<PrivateRoute element={<OptionalScanSteps />} />} />

                  <Route path={LINKS.YPO}              element={<PrivateRoute element={<YarnOrderList tableName={TABLE_NAME.ypoTable} />} />} />
                  <Route path={LINKS.PURCHASE_MANAGEMENT} element={<PrivateRoute element={<YarnOrderList tableName={TABLE_NAME.ypoTable} />} />} />
                  <Route path={LINKS.APO}              element={<PrivateRoute element={<AccessoryOrderList tableName={TABLE_NAME.apoTable} />} />} />
                  <Route path={LINKS.LSPPO}            element={<PrivateRoute element={<SubYarnOrderList tableName={TABLE_NAME.lsppoTable} />} />} />
                  <Route path={LINKS.YARN_FOLLOW_UP}   element={<PrivateRoute element={<FollowPage />} />} />
                  <Route path={LINKS.YARN_PACKING_LIST} element={<PrivateRoute element={<YarnPL />} />} />

                  <Route path={LINKS.FCD_YARN_DYE}     element={<PrivateRoute element={<FcdYarnDye />} />} />
                  <Route path={LINKS.FCD_PIECE_DYE}    element={<PrivateRoute element={<PieceDye />} />} />
                  <Route path={LINKS.PIECE_DYE_RECEIPE} element={<PrivateRoute element={<PieceDyeReceipe />} />} />
                  <Route path={LINKS.YARN_DYE_RECEIPE} element={<PrivateRoute element={<YarnDyeReceipe />} />}/>
                  <Route path={LINKS.GESTION_STOCK_RECIPE} element={<PrivateRoute element={<GestionStockReceipe />} />} />
                  <Route path={LINKS.RAPPORT_STOCK_RECEIPE} element={<PrivateRoute element={<RapportGestionReceipe />} />} />
                  

                  <Route path={LINKS.ORDER_HEAD}       element={<PrivateRoute element={<OrderHead tableName={TABLE_NAME.orderSheet} />} />} />
                  <Route path={LINKS.ORDER_STYLE}      element={<PrivateRoute element={<OrderStyle tableName={TABLE_NAME.orderStyle} />} />} />
                  <Route path={LINKS.BOOKING}          element={<PrivateRoute element={<Booking />} />} />

                  <Route path={LINKS.PDMS}             element={<PrivateRoute element={<Pdms tableName={TABLE_NAME.pdmsTable} />} />} />
                  <Route path={LINKS.PDMS_OPERATION}   element={<PrivateRoute element={<ProductionTicketScan />} />} />
                  <Route path={LINKS.PDMS_TICKET_DETAILS} element={<PrivateRoute element={<TicketScanDetails />} />} />
                  <Route path={LINKS.PDMS_MISSING_PIECES} element={<PrivateRoute element={<MissingPieces />} />} />
                  <Route
                    path={LINKS.PDMS_LEFT_OVER}
                    element={
                      <PrivateRoute
                        element={<LeftOver />}
                        user_functionalities={[E_FUNCTIONALITY.sub_menu_pdms_left_over]}
                      />
                    }
                  />
                  <Route
                    path={LINKS.PDMS_LEFT_OVER_CREATE}
                    element={
                      <PrivateRoute
                        element={<LeftOverCreate />}
                        user_functionalities={[
                          E_FUNCTIONALITY.sub_menu_pdms_left_over,
                          E_FUNCTIONALITY.btn_create_pdms_left_over,
                        ]}
                      />
                    }
                  />
                  <Route
                    path={LINKS.PDMS_QUANTITY_TAKEN}
                    element={
                      <PrivateRoute
                        element={<QuantityTaken />}
                        user_functionalities={[
                          E_FUNCTIONALITY.sub_menu_pdms_quantity_taken,
                        ]}
                      />
                    }
                  />
                  <Route
                    path={LINKS.PDMS_QUANTITY_TAKEN_CREATE}
                    element={
                      <PrivateRoute
                        element={<QuantityTakenCreate />}
                        user_functionalities={[
                          E_FUNCTIONALITY.sub_menu_pdms_quantity_taken,
                          E_FUNCTIONALITY.btn_create_pdms_quantity_taken,
                        ]}
                      />
                    }
                  />
                  <Route path={LINKS.PDMS_ACTIVATE_SCAN} element={<PrivateRoute element={<ActivateScan />} />} />

                  <Route path={LINKS.ACCOUNT_YARN}     element={<PrivateRoute element={<YarnPMList tableName={TABLE_NAME.ypoTable} />} />} />
                  <Route path={LINKS.ACCOUNT_ACCESSORY} element={<PrivateRoute element={<AccessoryPMList tableName={TABLE_NAME.apoTable} />} />} />
                  <Route path={LINKS.ACCOUNT_SUB_YARN} element={<PrivateRoute element={<LspPMList tableName={TABLE_NAME.lsppoTable} />} />} />

                  <Route path={LINKS.WAREHOUSE_YARN_INVOICE}     element={<PrivateRoute element={<YarnInvoice />} />} />
                  <Route path={LINKS.WAREHOUSE_ACCESSORY_INVOICE} element={<PrivateRoute element={<AccessoryInvoice />} />} />
                  <Route path={LINKS.W_YARN_RECEIVED_INVOICE}    element={<PrivateRoute element={<YarnReceivedInvoice />} />} />
                  <Route path={LINKS.W_ACC_RECEIVED_INVOICE}     element={<PrivateRoute element={<AccessoryReceivedInvoice />} />} />
              
                  <Route path={LINKS.WAREHOUSE_YARN_DELIVERY}    element={<PrivateRoute element={<DeliveryNoteListe />} />} />
                  <Route path={LINKS.WAREHOUSE_GARMENT_DELIVERY} element={<PrivateRoute element={<GarmentDNotesListe />} />} />
                  <Route path={LINKS.WAREHOUSE_ACC_DELIVERY}     element={<PrivateRoute element={<AccDeliveryNoteListe />} />} />
                  <Route path={LINKS.WAREHOUSE_FCD_DELIVERY}     element={<PrivateRoute element={<FcdDeliveryNoteListe />} />} />

                  <Route path={LINKS.WAREHOUSE_YARN_STOCK}       element={<PrivateRoute element={<YarnStock />} />} />
                  <Route path={LINKS.WAREHOUSE_ACC_STOCK}        element={<PrivateRoute element={<AccessoryStock />} />} />
                  <Route path={LINKS.WAREHOUSE_LYCRA_STOCK}      element={<PrivateRoute element={<LycraStock />} />} />
                  <Route path={LINKS.WAREHOUSE_FCD_STOCK}        element={<PrivateRoute element={<FcdStock />} />} />
                  <Route path={LINKS.WAREHOUSE_GARMENT_STOCK}    element={<PrivateRoute element={<GarmentStock />} />} />
                  <Route path={LINKS.WAREHOUSE_ADJUSTMENT}       element={<PrivateRoute element={<StockAdjustment />} />} />

                  <Route path="*" element={<NotFoundPage />} />
                  <Route path={LINKS.PDMS_YARN_ALLOCATION} element={<PrivateRoute element={<YarnRequirementAllocation />} />} />
                  <Route path={LINKS.PDMS_YARN_ALLOCATION_DETAIL} element={<PrivateRoute element={<YarnRequirementAllocationDetails />} />} />
                </Routes>
              </Suspense>
            </Router>
          </AuthProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
